<script setup>
// Sub-page chrome wrapper — when a domain has `settings.design_variant`
// pointing at one of the v01–v14 React variants, this wraps the page in
// the variant's real Nav + Footer (extracted into _vXX_chrome.jsx). Used
// to give every interior sub-page (SolarCalculator, Glossary, Marktanalyse,
// Contact, Partner, …) the same chrome the homepage shows for that
// variant — without rewriting the 420 generic pages.
//
// When `domain.settings.design_variant` is unset (or points at a Vue-side
// variant without dedicated React chrome), the wrapper renders just the
// slot, letting the parent AppLayout supply its standard header/footer.
//
// NOTE: import names match the actual exports in each `_vXX_chrome.jsx`
// file. v06 is `V06SoftConsumer` (capital C) — different from the slug
// shape `v06_softconsumer`.
import { computed } from 'vue';
import { applyPureReactInVue } from 'veaury';
import { V01TrustbankNav, V01TrustbankFooter } from '@/Variants/v01_trustbank/Chrome.jsx';
import { V02EditorialNav, V02EditorialFooter } from '@/Variants/v02_editorial/Chrome.jsx';
import { V03EarthyNav, V03EarthyFooter } from '@/Variants/v03_earthy/Chrome.jsx';
import { V04ModernistNav, V04ModernistFooter } from '@/Variants/v04_modernist/Chrome.jsx';
import { V05TechnicalNav, V05TechnicalFooter } from '@/Variants/v05_technical/Chrome.jsx';
import { V06SoftConsumerNav, V06SoftConsumerFooter } from '@/Variants/v06_softconsumer/Chrome.jsx';
import { V07SwissNav, V07SwissFooter } from '@/Variants/v07_swiss/Chrome.jsx';
import { V08CinematicNav, V08CinematicFooter } from '@/Variants/v08_cinematic/Chrome.jsx';
import { V09DirectresponseNav, V09DirectresponseFooter } from '@/Variants/v09_directresponse/Chrome.jsx';
import { V10MapledNav, V10MapledFooter } from '@/Variants/v10_mapled/Chrome.jsx';
import { V11ForumNav, V11ForumFooter } from '@/Variants/v11_forum/Chrome.jsx';
import { V12ConversationalNav, V12ConversationalFooter } from '@/Variants/v12_conversational/Chrome.jsx';
import { V13HandwerkerNav, V13HandwerkerFooter } from '@/Variants/v13_handwerker/Chrome.jsx';
import { V14ComparisonNav, V14ComparisonFooter } from '@/Variants/v14_comparison/Chrome.jsx';

// Bespoke pilot Vue chrome (per-host masthead + footer extracted from each
// pilot's Home component). Keyed by request host (lowercase, no www. prefix)
// so legal pages (Impressum/Datenschutz/AGB) render with the pilot's own
// visual chrome rather than the generic AppLayout header/footer.
import WwwSolarChrome from '@/MissionChrome/WwwSolarChrome.vue';
import EignungsAmpelChrome from '@/MissionChrome/EignungsAmpelChrome.vue';
import HausautarkieChrome from '@/MissionChrome/HausautarkieChrome.vue';
import HeizenmitwaermepumpeChrome from '@/MissionChrome/HeizenmitwaermepumpeChrome.vue';
import HeizungSolarBremenChrome from '@/MissionChrome/HeizungSolarBremenChrome.vue';
import HeizungsbauBremenChrome from '@/MissionChrome/HeizungsbauBremenChrome.vue';
import HeizungssucheChrome from '@/MissionChrome/HeizungssucheChrome.vue';
import HeizungZukunftChrome from '@/MissionChrome/HeizungZukunftChrome.vue';
import MySolar24Chrome from '@/MissionChrome/MySolar24Chrome.vue';
import PvCheckProChrome from '@/MissionChrome/PvCheckProChrome.vue';
import PvHeizungChrome from '@/MissionChrome/PvHeizungChrome.vue';
import PvKostenrechnerChrome from '@/MissionChrome/PvKostenrechnerChrome.vue';
import SolarheizthermChrome from '@/MissionChrome/SolarheizthermChrome.vue';
import SolarMillenniumChrome from '@/MissionChrome/SolarMillenniumChrome.vue';
import SolarPlanenChrome from '@/MissionChrome/SolarPlanenChrome.vue';
import SolarCheck26Chrome from '@/MissionChrome/SolarCheck26Chrome.vue';
import SolarangebotvergleichChrome from '@/MissionChrome/SolarangebotvergleichChrome.vue';
import WaermepumpensofortChrome from '@/MissionChrome/WaermepumpensofortChrome.vue';
import WaermepumpevergleichChrome from '@/MissionChrome/WaermepumpevergleichChrome.vue';
import WaermepreisChrome from '@/MissionChrome/WaermepreisChrome.vue';

const MISSION_CHROME_MAP = {
    'www-solar.de': WwwSolarChrome,
    'waermepumpenampel.de': EignungsAmpelChrome,
    'hausautarkie.com': HausautarkieChrome,
    'heizen-mit-waermepumpe.de': HeizenmitwaermepumpeChrome,
    'heizung-solar-bremen.de': HeizungSolarBremenChrome,
    'heizungsbaubremen.de': HeizungsbauBremenChrome,
    'heizungssuche.de': HeizungssucheChrome,
    'heizungzukunft.com': HeizungZukunftChrome,
    'my-solar24.de': MySolar24Chrome,
    'pvcheckpro.com': PvCheckProChrome,
    'pvheizung.de': PvHeizungChrome,
    'pvkostenrechner.com': PvKostenrechnerChrome,
    'solar-heiztherm.de': SolarheizthermChrome,
    'solar-millennium.de': SolarMillenniumChrome,
    'solar-planen.de': SolarPlanenChrome,
    'solarcheck26.de': SolarCheck26Chrome,
    'solar-angebot-vergleich.de': SolarangebotvergleichChrome,
    'waermepumpensofort.de': WaermepumpensofortChrome,
    'waermepumpevergleich.de': WaermepumpevergleichChrome,
    'waermepreis.com': WaermepreisChrome,
};

const props = defineProps({
    domain: { type: Object, default: null },
});

const variantSlug = computed(() => props.domain?.settings?.design_variant || null);

const NAV_MAP = {
    v01_trustbank: V01TrustbankNav,
    v02_editorial: V02EditorialNav,
    v03_earthy: V03EarthyNav,
    v04_modernist: V04ModernistNav,
    v05_technical: V05TechnicalNav,
    v06_softconsumer: V06SoftConsumerNav,
    v07_swiss: V07SwissNav,
    v08_cinematic: V08CinematicNav,
    v09_directresponse: V09DirectresponseNav,
    v10_mapled: V10MapledNav,
    v11_forum: V11ForumNav,
    v12_conversational: V12ConversationalNav,
    v13_handwerker: V13HandwerkerNav,
    v14_comparison: V14ComparisonNav,
};

const FOOTER_MAP = {
    v01_trustbank: V01TrustbankFooter,
    v02_editorial: V02EditorialFooter,
    v03_earthy: V03EarthyFooter,
    v04_modernist: V04ModernistFooter,
    v05_technical: V05TechnicalFooter,
    v06_softconsumer: V06SoftConsumerFooter,
    v07_swiss: V07SwissFooter,
    v08_cinematic: V08CinematicFooter,
    v09_directresponse: V09DirectresponseFooter,
    v10_mapled: V10MapledFooter,
    v11_forum: V11ForumFooter,
    v12_conversational: V12ConversationalFooter,
    v13_handwerker: V13HandwerkerFooter,
    v14_comparison: V14ComparisonFooter,
};

// Pre-wrap each React chrome component once at module load. Re-wrapping
// inside a `computed` would build a new Vue↔React boundary every render
// and reset chrome internal state.
const wrappedNav = {};
const wrappedFooter = {};
for (const slug of Object.keys(NAV_MAP)) {
    wrappedNav[slug] = applyPureReactInVue(NAV_MAP[slug]);
    wrappedFooter[slug] = applyPureReactInVue(FOOTER_MAP[slug]);
}

const NavComp = computed(() => wrappedNav[variantSlug.value] || null);
const FooterComp = computed(() => wrappedFooter[variantSlug.value] || null);

const brand = computed(() => {
    const d = props.domain;
    if (!d) return '';
    if (d.settings?.brand_name) return d.settings.brand_name;
    return (d.name || '').replace(/\.(de|com|net|org|solar)$/i, '');
});

// True only when we can actually render variant chrome (slug set AND
// chrome registered). Vue-side variants (waemo/profi/…) and unknown
// slugs fall through to the default slot path.
const hasVariantChrome = computed(() => !!(variantSlug.value && wrappedNav[variantSlug.value]));

// Bespoke pilot chrome takes precedence over v01..v14 React chrome and
// over default AppLayout chrome. Host comes from props.domain.request_host
// (preferred) or props.domain.name, case-insensitive, www. stripped.
const missionHost = computed(() => {
    const raw = props.domain?.request_host || props.domain?.name || '';
    return raw.toLowerCase().replace(/^www\./, '');
});
const missionChromeComp = computed(() => MISSION_CHROME_MAP[missionHost.value] || null);

// Per-host default accent. The global #0EA5E9 fallback only fires when a
// pilot has no DB brand-color AND no host-specific override; mission-chrome
// pilots register their canonical accent here so legal sub-pages match the
// homepage palette (e.g. SAV is yellow, not the generic sky-blue default).
const MISSION_DEFAULT_ACCENT = {
    'solar-angebot-vergleich.de': '#FACC15',
};

// Per-pilot brand props forwarded into the chrome component. Pilots read
// these from domain.settings and brand_* fields the controllers expose.
const missionChromeProps = computed(() => {
    const d = props.domain || {};
    const s = d.settings || {};
    const hostDefault = MISSION_DEFAULT_ACCENT[missionHost.value] || '#0EA5E9';
    return {
        domain: d,
        brandName: s.brand_name || d.name || '',
        brandTagline: s.brand_tagline || s.tagline || '',
        brandAccent: s.brand_accent || s.brand_color || hostDefault,
        brandCta: s.brand_cta || s.brand_accent || '#000000',
        author: s.author || null,
        designVersion: s.design_version || '',
    };
});
</script>

<template>
    <component
        v-if="missionChromeComp"
        :is="missionChromeComp"
        v-bind="missionChromeProps"
    >
        <slot />
    </component>
    <div v-else-if="hasVariantChrome">
        <component :is="NavComp" :brand="brand" />
        <main><slot /></main>
        <component :is="FooterComp" :brand="brand" :domain="domain" />
    </div>
    <slot v-else />
</template>
